import { ElementType } from '~/common/typescript/models/Element';
import { TerraDrawMode_Enum } from '~/components/MapDraw/TerraDrawUtil';

export type ShortcutCategory = 'camera' | 'drawing' | 'panels' | 'general';

export type ShortcutActionType =
  | 'TOGGLE_CHORD'
  | 'CANCEL_OR_ESCAPE'
  | 'ZOOM_DEFAULT_VIEW'
  | 'ZOOM_TO_DATA'
  | 'SAVE_DEFAULT_VIEW'
  | 'ZOOM_IN'
  | 'ZOOM_OUT'
  | 'DRAW_POINT'
  | 'DRAW_LINESTRING'
  | 'DRAW_POLYGON'
  | 'TOGGLE_LEGEND_PANEL'
  | 'TOGGLE_DATATABLE_PANEL'
  | 'TOGGLE_COMMENT_PANEL'
  | 'TOGGLE_GEOCOLLECT_PANEL'
  | 'TOGGLE_FOREST_WATCH_PANEL'
  | 'TOGGLE_UPLOAD_TASK_PANEL'
  | 'TOGGLE_MINIMAP'
  | 'TOGGLE_MAP_VIEW_PANEL'
  | 'TOGGLE_SEARCH_PANEL'
  | 'TOGGLE_NAVIGATOR_PANEL';

export interface KeyboardShortcutItem {
  id: ShortcutActionType;
  keys: string;
  chordKey: string;
  label: string;
  description: string;
  category: ShortcutCategory;
}

export const KEYBOARD_SHORTCUT_ITEMS: KeyboardShortcutItem[] = [
  // Camera & Góc nhìn
  {
    id: 'ZOOM_TO_DATA',
    keys: 'Ctrl + F',
    chordKey: 'F',
    label: 'Phóng tới dữ liệu',
    description: 'Phóng bản đồ bao trọn toàn bộ các lớp dữ liệu đang hiển thị',
    category: 'camera',
  },
  {
    id: 'ZOOM_DEFAULT_VIEW',
    keys: 'Ctrl + 0',
    chordKey: '0',
    label: 'Thu phóng mặc định',
    description: 'Quay về khung nhìn mặc định của dự án (hoặc toàn quốc nếu chưa thiết lập)',
    category: 'camera',
  },
  {
    id: 'SAVE_DEFAULT_VIEW',
    keys: 'Ctrl + S',
    chordKey: 'S',
    label: 'Lưu thu phóng mặc định',
    description: 'Lưu khung nhìn bản đồ hiện tại làm vùng hiển thị mặc định cho dự án',
    category: 'camera',
  },
  {
    id: 'ZOOM_IN',
    keys: 'Ctrl + + / =',
    chordKey: '+',
    label: 'Phóng to bản đồ',
    description: 'Tăng mức thu phóng (zoom in) thêm 1 cấp',
    category: 'camera',
  },
  {
    id: 'ZOOM_OUT',
    keys: 'Ctrl + -',
    chordKey: '-',
    label: 'Thu nhỏ bản đồ',
    description: 'Giảm mức thu phóng (zoom out) đi 1 cấp',
    category: 'camera',
  },

  // Công cụ vẽ đối tượng (Element)
  {
    id: 'DRAW_POINT',
    keys: 'P',
    chordKey: 'P',
    label: 'Vẽ Điểm (Point)',
    description: 'Kích hoạt chế độ vẽ đối tượng Điểm trên bản đồ',
    category: 'drawing',
  },
  {
    id: 'DRAW_LINESTRING',
    keys: 'L',
    chordKey: 'L',
    label: 'Vẽ Đường (Polyline)',
    description: 'Kích hoạt chế độ vẽ đối tượng Đường gấp khúc trên bản đồ',
    category: 'drawing',
  },
  {
    id: 'DRAW_POLYGON',
    keys: 'R',
    chordKey: 'R',
    label: 'Vẽ Vùng (Polygon)',
    description: 'Kích hoạt chế độ vẽ đối tượng Đa giác / Vùng trên bản đồ',
    category: 'drawing',
  },

  // Bảng điều khiển & Lớp hiển thị
  {
    id: 'TOGGLE_LEGEND_PANEL',
    keys: 'Alt + 1',
    chordKey: '1',
    label: 'Chú giải & Lớp bản đồ',
    description: 'Bật/tắt bảng Chú giải và danh sách lớp dữ liệu bản đồ',
    category: 'panels',
  },
  {
    id: 'TOGGLE_DATATABLE_PANEL',
    keys: 'Alt + 2',
    chordKey: '2',
    label: 'Bảng dữ liệu thuộc tính',
    description: 'Bật/tắt bảng dữ liệu thuộc tính phía dưới bản đồ',
    category: 'panels',
  },
  {
    id: 'TOGGLE_COMMENT_PANEL',
    keys: 'Alt + 3',
    chordKey: '3',
    label: 'Bảng ghi chú',
    description: 'Bật/tắt bảng thảo luận và ghi chú theo tọa độ',
    category: 'panels',
  },
  {
    id: 'TOGGLE_GEOCOLLECT_PANEL',
    keys: 'Alt + 4',
    chordKey: '4',
    label: 'Dữ liệu thu thập (Geocollect)',
    description: 'Bật/tắt bảng quản lý dữ liệu thực địa Geocollect',
    category: 'panels',
  },
  {
    id: 'TOGGLE_FOREST_WATCH_PANEL',
    keys: 'Alt + 5',
    chordKey: '5',
    label: 'Ảnh vệ tinh & Cháy rừng',
    description: 'Bật/tắt bảng tra cứu ảnh vệ tinh Sentinel-2, Landsat, Planet và điểm cháy',
    category: 'panels',
  },
  {
    id: 'TOGGLE_UPLOAD_TASK_PANEL',
    keys: 'Alt + 6',
    chordKey: '6',
    label: 'Tiến trình tải lên',
    description: 'Bật/tắt bảng theo dõi tiến trình xử lý và tạo lát gạch GIS',
    category: 'panels',
  },
  {
    id: 'TOGGLE_MINIMAP',
    keys: 'Alt + 7',
    chordKey: '7 / M',
    label: 'Bản đồ con (Minimap)',
    description: 'Ẩn/hiện bản đồ con định vị góc dưới bên trái',
    category: 'panels',
  },
  {
    id: 'TOGGLE_MAP_VIEW_PANEL',
    keys: 'Alt + V',
    chordKey: 'V',
    label: 'Bảng Hiển thị & Tính năng',
    description: 'Bật/tắt bảng điều khiển Hiển thị & công cụ góc nhìn',
    category: 'panels',
  },
  {
    id: 'TOGGLE_SEARCH_PANEL',
    keys: 'Ctrl + K, /',
    chordKey: '/',
    label: 'Tìm kiếm địa điểm',
    description: 'Bật/tắt thanh tìm kiếm địa chỉ và tọa độ',
    category: 'panels',
  },

  // Hệ thống & Điều hướng chung
  {
    id: 'TOGGLE_CHORD',
    keys: 'Ctrl + K',
    chordKey: 'Ctrl + K',
    label: 'Chế độ lắng nghe lệnh (Command Chord)',
    description: 'Mở thanh lắng nghe tổ hợp phím tắt nhanh trên bản đồ',
    category: 'general',
  },
  {
    id: 'TOGGLE_NAVIGATOR_PANEL',
    keys: '? / F1',
    chordKey: '? / H',
    label: 'Hướng dẫn & Bảng phím tắt',
    description: 'Mở bảng hướng dẫn điều hướng và danh mục phím tắt',
    category: 'general',
  },
  {
    id: 'CANCEL_OR_ESCAPE',
    keys: 'Esc',
    chordKey: 'Esc',
    label: 'Hủy lệnh / Thoát chế độ vẽ',
    description: 'Đóng menu ngữ cảnh, thoát chế độ vẽ đối tượng hoặc hủy lắng nghe Ctrl + K',
    category: 'general',
  },
];

const MODIFIER_KEYS = new Set(['Control', 'Meta', 'Shift', 'Alt', 'CapsLock', 'OS']);

/**
 * Checks whether the keyboard event originated inside an editable input, textarea,
 * contentEditable node, or rich-text/select widget so map shortcuts don't hijack typing.
 */
export function isEditableElement(target: any): boolean {
  if (!target || typeof target !== 'object') return false;

  const tagName = typeof target.tagName === 'string' ? target.tagName.toUpperCase() : '';
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return true;
  }

  if (target.isContentEditable === true) {
    return true;
  }

  if (typeof target.closest === 'function') {
    const editableAncestor = target.closest(
      'input, textarea, select, [contenteditable="true"], .tiptap, .ProseMirror, .sun-editor-editable, .ant-select-focused, .ant-input',
    );
    if (editableAncestor) {
      return true;
    }
  }

  return false;
}

/**
 * Maps GeoCloud ElementType to TerraDrawMode_Enum.
 */
export function mapElementTypeToTerraDrawMode(elementType: ElementType): TerraDrawMode_Enum {
  switch (elementType) {
    case ElementType.POINT:
      return TerraDrawMode_Enum.POINT;
    case ElementType.LINESTRING:
      return TerraDrawMode_Enum.LINESTRING;
    case ElementType.POLYGON:
      return TerraDrawMode_Enum.POLYGON;
    case ElementType.CIRCLE:
      return TerraDrawMode_Enum.CIRCLE;
    default:
      return TerraDrawMode_Enum.SELECT;
  }
}

/**
 * Safely parses a bounding box representation into [[west, south], [east, north]].
 * Supports:
 * - JSON string or nested array: `[[west, south], [east, north]]`
 * - Flat 4-number array or string: `[west, south, east, north]` or `"west,south,east,north"`
 */
export function parseBoundsTuple(
  raw: string | number[][] | number[] | null | undefined,
): [[number, number], [number, number]] | null {
  if (raw === null || raw === undefined) return null;

  let parsed: any = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('[')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return null;
      }
    } else if (trimmed.includes(',')) {
      const parts = trimmed.split(',').map((s) => Number(s.trim()));
      if (parts.length >= 4 && parts.slice(0, 4).every((n) => Number.isFinite(n))) {
        parsed = parts.slice(0, 4);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  let west: number;
  let south: number;
  let east: number;
  let north: number;

  if (
    Array.isArray(parsed) &&
    parsed.length === 2 &&
    Array.isArray(parsed[0]) &&
    Array.isArray(parsed[1]) &&
    parsed[0].length >= 2 &&
    parsed[1].length >= 2
  ) {
    west = Number(parsed[0][0]);
    south = Number(parsed[0][1]);
    east = Number(parsed[1][0]);
    north = Number(parsed[1][1]);
  } else if (Array.isArray(parsed) && parsed.length >= 4 && !Array.isArray(parsed[0])) {
    west = Number(parsed[0]);
    south = Number(parsed[1]);
    east = Number(parsed[2]);
    north = Number(parsed[3]);
  } else {
    return null;
  }

  if (
    !Number.isFinite(west) ||
    !Number.isFinite(south) ||
    !Number.isFinite(east) ||
    !Number.isFinite(north)
  ) {
    return null;
  }

  if (west < -180 || east > 180 || south < -90 || north > 90 || west > east || south > north) {
    return null;
  }

  return [
    [west, south],
    [east, north],
  ];
}

/**
 * Combines multiple bounding box strings/tuples into a single encompassing [[west, south], [east, north]] tuple.
 */
export function combineBoundsTuples(
  boundsList: Array<string | number[][] | number[] | null | undefined>,
): [[number, number], [number, number]] | null {
  let minWest = Infinity;
  let minSouth = Infinity;
  let maxEast = -Infinity;
  let maxNorth = -Infinity;
  let count = 0;

  for (const item of boundsList) {
    const tuple = parseBoundsTuple(item);
    if (!tuple) continue;
    const [[w, s], [e, n]] = tuple;
    if (w < minWest) minWest = w;
    if (s < minSouth) minSouth = s;
    if (e > maxEast) maxEast = e;
    if (n > maxNorth) maxNorth = n;
    count++;
  }

  if (count === 0) return null;
  return [
    [minWest, minSouth],
    [maxEast, maxNorth],
  ];
}

export interface ShortcutEventInput {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
}

export interface ShortcutResolution {
  action: ShortcutActionType | null;
  nextChordState: boolean;
  preventDefault: boolean;
}

/**
 * Pure state-machine resolver for GeoCloud Map keyboard shortcuts.
 */
export function resolveShortcutAction(
  event: ShortcutEventInput,
  isChordActive: boolean,
  isEditingInput: boolean,
): ShortcutResolution {
  const rawKey = event.key || '';
  const lowerKey = rawKey.toLowerCase();
  const hasCmdOrCtrl = Boolean(event.ctrlKey || event.metaKey);
  const hasAlt = Boolean(event.altKey);

  // Ignore standalone modifier keydowns so holding Ctrl/Shift during a chord doesn't cancel it
  if (MODIFIER_KEYS.has(rawKey)) {
    return {
      action: null,
      nextChordState: isChordActive,
      preventDefault: false,
    };
  }

  // 1. Ctrl/Cmd + K always toggles the chord listener (even if focused in input, or outside)
  if (hasCmdOrCtrl && !hasAlt && lowerKey === 'k') {
    return {
      action: 'TOGGLE_CHORD',
      nextChordState: !isChordActive,
      preventDefault: true,
    };
  }

  // 2. If user is typing inside an input/textarea/editor, only allow Escape to cancel active chord
  if (isEditingInput) {
    if (rawKey === 'Escape' && isChordActive) {
      return {
        action: 'CANCEL_OR_ESCAPE',
        nextChordState: false,
        preventDefault: true,
      };
    }
    return {
      action: null,
      nextChordState: false,
      preventDefault: false,
    };
  }

  // 3. When Ctrl + K chord mode is active, accept either bare key or Ctrl/Cmd + key
  if (isChordActive) {
    if (rawKey === 'Escape') {
      return { action: 'CANCEL_OR_ESCAPE', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '0') {
      return { action: 'ZOOM_DEFAULT_VIEW', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'f') {
      return { action: 'ZOOM_TO_DATA', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 's') {
      return { action: 'SAVE_DEFAULT_VIEW', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '+' || rawKey === '=') {
      return { action: 'ZOOM_IN', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '-' || rawKey === '_') {
      return { action: 'ZOOM_OUT', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'p') {
      return { action: 'DRAW_POINT', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'l') {
      return { action: 'DRAW_LINESTRING', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'r') {
      return { action: 'DRAW_POLYGON', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '1') {
      return { action: 'TOGGLE_LEGEND_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '2') {
      return { action: 'TOGGLE_DATATABLE_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '3') {
      return { action: 'TOGGLE_COMMENT_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '4') {
      return { action: 'TOGGLE_GEOCOLLECT_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '5') {
      return { action: 'TOGGLE_FOREST_WATCH_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '6') {
      return { action: 'TOGGLE_UPLOAD_TASK_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '7' || lowerKey === 'm') {
      return { action: 'TOGGLE_MINIMAP', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'v') {
      return { action: 'TOGGLE_MAP_VIEW_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '/') {
      return { action: 'TOGGLE_SEARCH_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '?' || lowerKey === 'h' || rawKey === 'F1') {
      return { action: 'TOGGLE_NAVIGATOR_PANEL', nextChordState: false, preventDefault: true };
    }

    // Unrecognized key while in chord mode -> exit chord mode
    return {
      action: null,
      nextChordState: false,
      preventDefault: false,
    };
  }

  // 4. Direct Shortcuts with Ctrl / Cmd (Camera & View actions)
  if (hasCmdOrCtrl && !hasAlt) {
    if (rawKey === '0') {
      return { action: 'ZOOM_DEFAULT_VIEW', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'f') {
      return { action: 'ZOOM_TO_DATA', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 's') {
      return { action: 'SAVE_DEFAULT_VIEW', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '+' || rawKey === '=') {
      return { action: 'ZOOM_IN', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '-' || rawKey === '_') {
      return { action: 'ZOOM_OUT', nextChordState: false, preventDefault: true };
    }
    return { action: null, nextChordState: false, preventDefault: false };
  }

  // 5. Direct Shortcuts with Alt (Panel toggles: Alt + 1..7, Alt + V)
  if (hasAlt && !hasCmdOrCtrl) {
    if (rawKey === '1') {
      return { action: 'TOGGLE_LEGEND_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '2') {
      return { action: 'TOGGLE_DATATABLE_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '3') {
      return { action: 'TOGGLE_COMMENT_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '4') {
      return { action: 'TOGGLE_GEOCOLLECT_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '5') {
      return { action: 'TOGGLE_FOREST_WATCH_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '6') {
      return { action: 'TOGGLE_UPLOAD_TASK_PANEL', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '7') {
      return { action: 'TOGGLE_MINIMAP', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'v') {
      return { action: 'TOGGLE_MAP_VIEW_PANEL', nextChordState: false, preventDefault: true };
    }
    return { action: null, nextChordState: false, preventDefault: false };
  }

  // 6. Single-key shortcuts (no Ctrl/Cmd/Alt)
  if (!hasCmdOrCtrl && !hasAlt) {
    if (rawKey === 'Escape') {
      return { action: 'CANCEL_OR_ESCAPE', nextChordState: false, preventDefault: false };
    }
    if (rawKey === '+' || rawKey === '=') {
      return { action: 'ZOOM_IN', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '-' || rawKey === '_') {
      return { action: 'ZOOM_OUT', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'p') {
      return { action: 'DRAW_POINT', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'l') {
      return { action: 'DRAW_LINESTRING', nextChordState: false, preventDefault: true };
    }
    if (lowerKey === 'r') {
      return { action: 'DRAW_POLYGON', nextChordState: false, preventDefault: true };
    }
    if (rawKey === '?' || rawKey === 'F1') {
      return { action: 'TOGGLE_NAVIGATOR_PANEL', nextChordState: false, preventDefault: true };
    }
  }

  return { action: null, nextChordState: false, preventDefault: false };
}

/**
 * Normalizes Vietnamese strings (removing diacritics and lowercasing) for search filtering.
 */
export function normalizeShortcutSearchText(input: string): string {
  if (!input) return '';
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
}

/**
 * Filters `KEYBOARD_SHORTCUT_ITEMS` by category ('all' or a specific `ShortcutCategory`)
 * and free-text search query (matching label, description, keys, chordKey, or id).
 */
export function filterShortcutItems(
  items: KeyboardShortcutItem[],
  query: string = '',
  category: 'all' | ShortcutCategory = 'all',
): KeyboardShortcutItem[] {
  const normalizedQuery = normalizeShortcutSearchText(query);

  return items.filter((item) => {
    if (category !== 'all' && item.category !== category) {
      return false;
    }
    if (!normalizedQuery) {
      return true;
    }
    const searchable = normalizeShortcutSearchText(
      `${item.label} ${item.description} ${item.keys} ${item.chordKey} ${item.id}`,
    );
    return searchable.includes(normalizedQuery);
  });
}

